/**
 * SiteFooter: the full footer on reading pages (home, sign-in, receipts…).
 * Map workspaces skip it — every pixel there goes to the map.
 *
 * Links only point at routes that exist; the brand mark reuses the tenant's
 * district colors like the header does.
 */
import { Link } from 'react-router-dom';
import { useTenantStore } from '../stores/tenant';
import { selectIsAuthenticated, useAuthStore } from '../stores/auth';

function FooterLink({ to, children }: { to: string; children: React.ReactNode }): JSX.Element {
  return (
    <li>
      <Link to={to} className="text-sm text-white/70 transition-colors hover:text-white">
        {children}
      </Link>
    </li>
  );
}

export function SiteFooter(): JSX.Element {
  const tenant = useTenantStore((s) => s.tenant);
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const colors = tenant?.districts.colors ?? [];
  const mark = [colors[0] ?? '#2F6DB5', colors[1] ?? '#E8912D', colors[2] ?? '#1E9E78', colors[3] ?? '#D1543A'];
  const year = new Date().getFullYear();

  return (
    <footer className="mt-8 bg-tenant-strong text-white">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:px-6 md:grid-cols-[1.4fr_1fr_1fr_1fr] lg:px-8">
        <div>
          <Link to="/" className="inline-flex items-center gap-2.5" aria-label="CivicLines home">
            <span aria-hidden="true" className="grid h-8 w-8 grid-cols-2 gap-[2px] rounded-lg bg-white/90 p-[3px]">
              {mark.map((c, i) => (
                <span key={i} className="rounded-[2px]" style={{ backgroundColor: c }} />
              ))}
            </span>
            <span className="text-lg font-extrabold tracking-tight">CivicLines</span>
          </Link>
          <p className="mt-3 max-w-xs text-sm leading-relaxed text-white/65">
            Public participation for local redistricting — draw district plans and share the
            communities that should stay together.
          </p>
          {tenant && (
            <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white/80">
              <span className="h-1.5 w-1.5 rounded-full bg-tenant-accent" aria-hidden="true" />
              Serving {tenant.name}
            </p>
          )}
        </div>

        <nav aria-label="Participate">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.12em] text-white/50">Participate</h2>
          <ul className="mt-3 space-y-2">
            <FooterLink to="/map">Draw a district plan</FooterLink>
            <FooterLink to="/coi">Share your community</FooterLink>
            <FooterLink to="/#how-it-works">How it works</FooterLink>
          </ul>
        </nav>

        <nav aria-label="Account">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.12em] text-white/50">Account</h2>
          <ul className="mt-3 space-y-2">
            {isAuthenticated ? (
              <FooterLink to="/map">My plans</FooterLink>
            ) : (
              <>
                <FooterLink to="/signin">Sign in</FooterLink>
                <FooterLink to="/signup">Create a free account</FooterLink>
                <FooterLink to="/forgot-password">Reset password</FooterLink>
              </>
            )}
          </ul>
        </nav>

        <nav aria-label="Staff">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.12em] text-white/50">For staff</h2>
          <ul className="mt-3 space-y-2">
            <FooterLink to="/admin">Staff sign-in</FooterLink>
          </ul>
        </nav>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-5 text-xs text-white/55 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <p>© {year} CivicLines</p>
          <p className="max-w-2xl sm:text-right">
            Maps and data shown are drafts for public comment — not adopted boundaries. Final maps
            are approved through {tenant ? `${tenant.name}'s` : 'your jurisdiction’s'} public hearing
            process.
          </p>
        </div>
      </div>
    </footer>
  );
}
